import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Input, Label, Row, Col, Table, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import Header from "../../../layout/Header/Header";
import { pdfDocumentoTabla, pdfFooter, pdfCliente, pdfDireccion, fmtMoney } from "../../../../pdfUtils";

const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const entero = (v) => new Intl.NumberFormat("en-US").format(v || 0);
const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v || 0);
const pendiente = (l) => l.cantidad - l.vendido - l.devuelto;

function ListadoConsignaciones() {
  const { user } = useContext(AuthContext);
  const URL_CONSIGNACIONES = process.env.REACT_APP_URL_CONSIGNACIONES;

  const [consignaciones, setConsignaciones] = useState([]);
  const [estatus, setEstatus] = useState("Abierta");
  const [busca, setBusca] = useState("");
  const [liquidando, setLiquidando] = useState(null);
  const [captura, setCaptura] = useState({});
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [guardando, setGuardando] = useState(false);

  function carga() {
    axios.get(URL_CONSIGNACIONES, auth()).then((r) => setConsignaciones(r.data)).catch(console.log);
  }
  useEffect(carga, [URL_CONSIGNACIONES]);

  const lista = useMemo(() => {
    const q = busca.toLowerCase();
    return consignaciones.filter(
      (c) =>
        (!estatus || c.estatus === estatus) &&
        (!q || `${c.idConsignacion} ${c.clientes[0]?.nombre_comercial}`.toLowerCase().includes(q))
    );
  }, [consignaciones, estatus, busca]);

  const abiertas = consignaciones.filter((c) => c.estatus === "Abierta");
  const lineasAbiertas = abiertas.flatMap((c) => c.lineas);
  const botellasFuera = lineasAbiertas.reduce((t, l) => t + pendiente(l), 0);
  const valorFuera = lineasAbiertas.reduce((t, l) => t + pendiente(l) * l.precio, 0);

  function abrirLiquidar(c) {
    setLiquidando(c);
    setFecha(moment().format("YYYY-MM-DD"));
    setCaptura({});
  }

  const cambia = (id, campo, valor) => setCaptura((prev) => ({ ...prev, [id]: { ...prev[id], [campo]: valor } }));
  const valorDe = (id, campo) => captura[id]?.[campo] ?? "";
  const excedido = (l) => (Number(valorDe(l._id, "vendido")) || 0) + (Number(valorDe(l._id, "devuelto")) || 0) > pendiente(l);

  const importeVendido = liquidando
    ? liquidando.lineas.reduce((t, l) => t + (Number(valorDe(l._id, "vendido")) || 0) * l.precio, 0)
    : 0;

  async function liquidar() {
    const renglones = Object.entries(captura).map(([id, v]) => ({ id, vendido: Number(v.vendido) || 0, devuelto: Number(v.devuelto) || 0 }));
    setGuardando(true);
    try {
      const { data } = await axios.patch(`${URL_CONSIGNACIONES.replace(/consignaciones$/, "consignacionesLiquidar")}/${liquidando._id}`, { fecha, renglones }, auth());
      setLiquidando(null);
      carga();
      Swal.fire("Listo", data.nota ? `Se genero la nota ${data.nota} por ${money(data.total)}` : "Devolucion registrada", "success");
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se liquido", text: error.response?.data?.message || "Intenta de nuevo" });
    }
    setGuardando(false);
  }

  async function cancelar(c) {
    const ok = await Swal.fire({
      title: `¿Cancelar la consignacion ${c.idConsignacion}?`,
      text: "Todo el vino regresa a la cava",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
    });
    if (!ok.value) return;
    try {
      await axios.patch(`${URL_CONSIGNACIONES.replace(/consignaciones$/, "consignacionesCancelar")}/${c._id}`, {}, auth());
      carga();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se cancelo", text: error.response?.data?.message || "Intenta de nuevo" });
    }
  }

  function pdf(c) {
    const cli = c.clientes[0] || {};
    const doc = new jsPDF({ compressPdf: true });
    pdfDocumentoTabla(doc, {
      titulo: "Nota de consignación",
      folio: c.idConsignacion,
      fecha: c.fecha,
      datos: [
        { titulo: "Cliente", lineas: pdfCliente(cli.nombre_comercial, cli.telefono, pdfDireccion(cli.calle, cli.numero_ext, cli.colonia, cli.delegacion, cli.ciudad, cli.estado, cli.cp)) },
        { titulo: "Datos", lineas: [`Almacen ${c.areas[0]?.name || ""}`, `Plazo ${c.plazo} dias`] },
      ],
      head: ["Vino", "Descripcion", "Botellas", "Vendido", "Devuelto", "En cliente", "Precio"],
      body: c.lineas.map((l) => [l.articulos?.codigo, l.articulos?.nombre, entero(l.cantidad), entero(l.vendido), entero(l.devuelto), entero(pendiente(l)), fmtMoney(l.precio)]),
      totales: [
        ["Botellas", entero(c.total_cantidad)],
        ["En cliente", entero(c.lineas.reduce((t, l) => t + pendiente(l), 0))],
        ["Valor antes de impuestos", fmtMoney(c.valor)],
      ],
      observaciones: c.observaciones,
      firmas: ["Entrego", "Recibio"],
    });
    pdfFooter(doc);
    doc.save(`Consignacion-${c.idConsignacion}.pdf`);
  }

  if (!user.menu_consignaciones) return <Header />;

  return (
    <>
      <Header />
      <br />
      <br />
      <div className="card col-12">
        <Row>
          <Col md={8}>
            <Button size="sm" href="/MenuVentas" className="btn btn-danger" id="botonListado">Regresar</Button>{" "}
            {user.consignaciones_create ? (
              <Button size="sm" href="/ConsignacionCreate" color="success">Nueva Consignación</Button>
            ) : null}
          </Col>
        </Row>
        <h3 align="center">Consignaciones</h3>
        <Row>
          <Col md={2}>
            <Label>Estatus</Label>
            <Input bsSize="sm" type="select" value={estatus} onChange={(e) => setEstatus(e.target.value)}>
              <option value="">Todas</option>
              <option value="Abierta">Abiertas</option>
              <option value="Cerrada">Cerradas</option>
              <option value="Cancelada">Canceladas</option>
            </Input>
          </Col>
          <Col md={3}>
            <Label>Buscar</Label>
            <Input bsSize="sm" placeholder="Folio o cliente" value={busca} onChange={(e) => setBusca(e.target.value)} />
          </Col>
          <Col md={7} className="d-flex align-items-end justify-content-end">
            <span>
              Con clientes: <strong>{entero(botellasFuera)}</strong> botellas por <strong>{money(valorFuera)}</strong> antes de impuestos
            </span>
          </Col>
        </Row>
        <br />
        <Table size="sm" striped borderless className="table-responsive-xl">
          <thead>
            <tr>
              <th>Numero</th>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Botellas</th>
              <th>Vendidas</th>
              <th>Devueltas</th>
              <th>En cliente</th>
              <th>Valor</th>
              <th>Estatus</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((c) => {
              const vendidas = c.lineas.reduce((t, l) => t + l.vendido, 0);
              const devueltas = c.lineas.reduce((t, l) => t + l.devuelto, 0);
              return (
                <tr key={c._id}>
                  <td>{c.idConsignacion}</td>
                  <td>{c.fecha}</td>
                  <td>{c.clientes[0]?.nombre_comercial}</td>
                  <td>{entero(c.total_cantidad)}</td>
                  <td>{entero(vendidas)}</td>
                  <td>{entero(devueltas)}</td>
                  <td>{entero(c.total_cantidad - vendidas - devueltas)}</td>
                  <td>{money(c.valor)}</td>
                  <td>{c.estatus}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Button size="sm" color="danger" title="PDF" disabled={!user.imprimir} onClick={() => pdf(c)}>
                      <i className="far fa-file-pdf" />
                    </Button>{" "}
                    {c.estatus === "Abierta" && user.consignaciones_liquidar ? (
                      <Button size="sm" color="success" title="Liquidar: registrar lo vendido y lo devuelto" onClick={() => abrirLiquidar(c)}>
                        <i className="fas fa-dollar-sign" /> Liquidar
                      </Button>
                    ) : null}{" "}
                    {c.estatus === "Abierta" && user.consignaciones_cancelar && !vendidas && !devueltas ? (
                      <Button size="sm" color="secondary" title="Cancelar consignacion" onClick={() => cancelar(c)}>
                        <i className="fas fa-ban" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        {lista.length === 0 ? <p align="center">No hay consignaciones con ese filtro</p> : null}
      </div>

      <Modal size="lg" isOpen={!!liquidando} toggle={() => setLiquidando(null)}>
        <ModalHeader toggle={() => setLiquidando(null)}>
          <span>Liquidar consignación {liquidando?.idConsignacion}: {liquidando?.clientes[0]?.nombre_comercial}</span>
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={4}>
              <Label>Fecha de la nota</Label>
              <Input bsSize="sm" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </Col>
          </Row>
          <br />
          <Table size="sm" borderless>
            <thead>
              <tr>
                <th>Vino</th>
                <th>En cliente</th>
                <th>Precio</th>
                <th>Vendido</th>
                <th>Devuelto</th>
              </tr>
            </thead>
            <tbody>
              {liquidando?.lineas.filter((l) => pendiente(l) > 0).map((l) => (
                <tr key={l._id}>
                  <td>{l.articulos?.codigo} {l.articulos?.nombre}</td>
                  <td>{entero(pendiente(l))}</td>
                  <td>{money(l.precio)}</td>
                  <td>
                    <Input bsSize="sm" type="number" min="0" name="vendido" invalid={excedido(l)} value={valorDe(l._id, "vendido")} onChange={(e) => cambia(l._id, "vendido", e.target.value)} />
                  </td>
                  <td>
                    <Input bsSize="sm" type="number" min="0" name="devuelto" invalid={excedido(l)} value={valorDe(l._id, "devuelto")} onChange={(e) => cambia(l._id, "devuelto", e.target.value)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <p className="mb-0">
            Lo vendido genera una nota por <strong>{money(importeVendido)}</strong> antes de impuestos; lo devuelto regresa a la cava.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" color="success" disabled={guardando || liquidando?.lineas.some(excedido)} onClick={liquidar}>
            Guardar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default ListadoConsignaciones;
